import type { ParseKeys, TFunction } from 'i18next';
import type { Timestamp } from '@framework/Timestamp';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 31 * DAY_MS;
const YEAR_MS = 12 * MONTH_MS;

// 粒度ごとの完全 i18n キー。t() には常に静的な完全キーを渡す方針のため、
// 呼び出し側がこの表を用意する (locales/README.md 参照)
export type RelativeTimeI18nKeys = Record<'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years', ParseKeys>;

/**
 * 対象日時と現在日時の差分から「◯分前」「◯時間前」などの相対時刻文字列を返します。
 */
export const formatRelativeTime = (
  target: Timestamp,
  now: Timestamp,
  t: TFunction,
  keys: RelativeTimeI18nKeys
): string => {
  const diffMs = now.toUnixTimestamp() - target.toUnixTimestamp();

  if (diffMs < HOUR_MS) {
    const count = Math.max(1, Math.floor(diffMs / MINUTE_MS));
    return t(keys.minutes, { count });
  }

  if (diffMs < DAY_MS) {
    const count = Math.floor(diffMs / HOUR_MS);
    return t(keys.hours, { count });
  }

  if (diffMs < WEEK_MS) {
    const count = Math.floor(diffMs / DAY_MS);
    return t(keys.days, { count });
  }

  if (diffMs < MONTH_MS) {
    const count = Math.floor(diffMs / WEEK_MS);
    return t(keys.weeks, { count });
  }

  if (diffMs < YEAR_MS) {
    const count = Math.floor(diffMs / MONTH_MS);
    return t(keys.months, { count });
  }

  const count = Math.floor(diffMs / YEAR_MS);
  return t(keys.years, { count });
};
